import Navigation from "../components/Navigation";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const Digital = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top immediately when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    if (location.pathname === "/") {
      // On home page, scroll to contact section
      const element = document.querySelector("#contact");
      if (element) {
        const navHeight = 80;
        const elementPosition = element.offsetTop - navHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });
        window.history.pushState(null, null, "#contact");
      }
    } else {
      // On other pages, navigate to home page with contact section
      navigate("/#contact");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContactClick();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Skip to main content
      </a>

      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy1.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy1.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy1.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy1.heroResults.results.hoursSaved.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy1.heroResults.results.hoursSaved.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy1.heroResults.results.processingTime.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy1.heroResults.results.processingTime.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy1.heroResults.results.ticketReduction.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy1.heroResults.results.ticketReduction.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy1.heroResults.results.automatedTransactions.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy1.heroResults.results.automatedTransactions.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy1.heroResults.results.powerBIShare.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy1.heroResults.results.powerBIShare.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy1.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("digital.caseStudy1.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy1.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("digital.caseStudy1.complication.issues.requests.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.complication.issues.requests.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("digital.caseStudy1.complication.issues.workflow.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.complication.issues.workflow.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy1.complication.issues.transparency.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.complication.issues.transparency.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("digital.caseStudy1.complication.issues.errors.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.complication.issues.errors.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy1.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("digital.caseStudy1.catalyst.description")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                    <thead className="bg-[#0F1B2D] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t(
                            "digital.caseStudy1.catalyst.tableHeaders.component"
                          )}
                        </th>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t(
                            "digital.caseStudy1.catalyst.tableHeaders.implementation"
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy1.catalyst.components.powerApps.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy1.catalyst.components.powerApps.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy1.catalyst.components.powerAutomate.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy1.catalyst.components.powerAutomate.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy1.catalyst.components.azureSql.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy1.catalyst.components.azureSql.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy1.catalyst.components.powerBI.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy1.catalyst.components.powerBI.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy1.catalyst.components.outlookTeams.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy1.catalyst.components.outlookTeams.description"
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("digital.caseStudy1.catalyst.zeroCode.title")}
                  </div>
                  <p className="text-gray-700">
                    {t("digital.caseStudy1.catalyst.zeroCode.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy1.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("digital.caseStudy1.impact.tableHeaders.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy1.impact.tableHeaders.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy1.impact.tableHeaders.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy1.impact.tableHeaders.change")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy1.impact.metrics.processingTime.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.processingTime.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.processingTime.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy1.impact.metrics.processingTime.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy1.impact.metrics.ticketVolume.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.ticketVolume.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.ticketVolume.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy1.impact.metrics.ticketVolume.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy1.impact.metrics.automatedTransactions.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.automatedTransactions.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.automatedTransactions.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy1.impact.metrics.automatedTransactions.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy1.impact.metrics.hoursSaved.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.hoursSaved.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.hoursSaved.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy1.impact.metrics.hoursSaved.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy1.impact.metrics.powerBIShare.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.powerBIShare.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy1.impact.metrics.powerBIShare.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy1.impact.metrics.powerBIShare.change"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy1.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy1.successFactors.factors.lowCode.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.successFactors.factors.lowCode.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy1.successFactors.factors.liveInsights.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.successFactors.factors.liveInsights.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy1.successFactors.factors.validationLogic.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.successFactors.factors.validationLogic.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy1.successFactors.factors.governance.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy1.successFactors.factors.governance.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("digital.caseStudy1.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("digital.caseStudy1.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("digital.caseStudy1.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("digital.caseStudy1.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                onKeyDown={handleKeyDown}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                {t("digital.caseStudy1.callToAction.button")}
              </button>
            </div>

            {/* Sources */}
            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <h4 className="font-semibold text-[#0F1B2D] mb-3">
                {t("digital.caseStudy1.sources.title")}
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {t("digital.caseStudy1.sources.sources", {
                  returnObjects: true,
                }).map((source, index) => (
                  <li key={index}>{source}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-4 border-gray-200 my-16"></div>

          {/* Second Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy2.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy2.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy2.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy2.heroResults.results.planningTime.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy2.heroResults.results.planningTime.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy2.heroResults.results.shiftConflicts.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy2.heroResults.results.shiftConflicts.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy2.heroResults.results.lastMinuteChanges.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy2.heroResults.results.lastMinuteChanges.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy2.heroResults.results.managementHours.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy2.heroResults.results.managementHours.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy2.heroResults.results.employeeNPS.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy2.heroResults.results.employeeNPS.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t(
                      "digital.caseStudy2.heroResults.results.employeeNPS.detail"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy2.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("digital.caseStudy2.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy2.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy2.complication.issues.timeConsumption.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.complication.issues.timeConsumption.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy2.complication.issues.frequentErrors.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.complication.issues.frequentErrors.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy2.complication.issues.noTransparency.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.complication.issues.noTransparency.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy2.complication.issues.spontaneousAbsences.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.complication.issues.spontaneousAbsences.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy2.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("digital.caseStudy2.catalyst.description")}
                </p>
                <div className="space-y-4">
                  {t("digital.caseStudy2.catalyst.features", {
                    returnObjects: true,
                  }).map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                      <div>{feature}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy2.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("digital.caseStudy2.impact.tableHeaders.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy2.impact.tableHeaders.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy2.impact.tableHeaders.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy2.impact.tableHeaders.change")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy2.impact.metrics.planningEffort.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.planningEffort.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.planningEffort.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy2.impact.metrics.planningEffort.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy2.impact.metrics.shiftConflicts.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.shiftConflicts.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.shiftConflicts.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy2.impact.metrics.shiftConflicts.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy2.impact.metrics.lastMinuteChanges.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.lastMinuteChanges.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.lastMinuteChanges.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy2.impact.metrics.lastMinuteChanges.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy2.impact.metrics.managementHours.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.managementHours.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.managementHours.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy2.impact.metrics.managementHours.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy2.impact.metrics.employeeNPS.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.employeeNPS.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy2.impact.metrics.employeeNPS.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy2.impact.metrics.employeeNPS.change"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy2.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy2.successFactors.factors.realTimeValidation.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.successFactors.factors.realTimeValidation.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy2.successFactors.factors.selfService.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.successFactors.factors.selfService.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy2.successFactors.factors.kpiDashboards.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.successFactors.factors.kpiDashboards.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy2.successFactors.factors.modularTechStack.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy2.successFactors.factors.modularTechStack.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("digital.caseStudy2.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("digital.caseStudy2.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("digital.caseStudy2.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("digital.caseStudy2.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                onKeyDown={handleKeyDown}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                {t("digital.caseStudy2.callToAction.button")}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-4 border-gray-200 my-16"></div>

          {/* Third Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy3.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy3.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy3.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy3.heroResults.results.hoursSaved.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy3.heroResults.results.hoursSaved.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy3.heroResults.results.reportingTime.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy3.heroResults.results.reportingTime.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t(
                      "digital.caseStudy3.heroResults.results.reportingTime.detail"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy3.heroResults.results.selfService.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy3.heroResults.results.selfService.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy3.heroResults.results.dataSources.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy3.heroResults.results.dataSources.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy3.heroResults.results.lastMinuteFlights.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy3.heroResults.results.lastMinuteFlights.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "digital.caseStudy3.heroResults.results.developmentTime.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "digital.caseStudy3.heroResults.results.developmentTime.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy3.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("digital.caseStudy3.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy3.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy3.complication.issues.timeDelay.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.complication.issues.timeDelay.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy3.complication.issues.dataBreaks.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.complication.issues.dataBreaks.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("digital.caseStudy3.complication.issues.peakLoad.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.complication.issues.peakLoad.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "digital.caseStudy3.complication.issues.blindSpots.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.complication.issues.blindSpots.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy3.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("digital.caseStudy3.catalyst.description")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                    <thead className="bg-[#0F1B2D] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t(
                            "digital.caseStudy3.catalyst.tableHeaders.component"
                          )}
                        </th>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t(
                            "digital.caseStudy3.catalyst.tableHeaders.implementation"
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerQuery.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerQuery.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy3.catalyst.components.azureSql.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy3.catalyst.components.azureSql.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerBIDataset.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerBIDataset.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerBIService.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerBIService.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerAutomate.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy3.catalyst.components.powerAutomate.description"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "digital.caseStudy3.catalyst.components.zeroCode.title"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "digital.caseStudy3.catalyst.components.zeroCode.description"
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("digital.caseStudy3.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("digital.caseStudy3.impact.tableHeaders.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy3.impact.tableHeaders.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy3.impact.tableHeaders.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("digital.caseStudy3.impact.tableHeaders.change")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy3.impact.metrics.reportingTime.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.reportingTime.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.reportingTime.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy3.impact.metrics.reportingTime.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy3.impact.metrics.manualHours.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.manualHours.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.manualHours.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy3.impact.metrics.manualHours.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy3.impact.metrics.selfServiceCalls.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.selfServiceCalls.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.selfServiceCalls.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy3.impact.metrics.selfServiceCalls.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy3.impact.metrics.lastMinuteFlights.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.lastMinuteFlights.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.lastMinuteFlights.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy3.impact.metrics.lastMinuteFlights.change"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "digital.caseStudy3.impact.metrics.economyVsBusiness.metric"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.economyVsBusiness.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "digital.caseStudy3.impact.metrics.economyVsBusiness.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "digital.caseStudy3.impact.metrics.economyVsBusiness.change"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("digital.caseStudy3.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy3.successFactors.factors.lowCodeFirst.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.successFactors.factors.lowCodeFirst.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy3.successFactors.factors.unifiedDataModel.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.successFactors.factors.unifiedDataModel.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy3.successFactors.factors.liveInsights.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.successFactors.factors.liveInsights.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "digital.caseStudy3.successFactors.factors.governanceByDesign.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "digital.caseStudy3.successFactors.factors.governanceByDesign.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("digital.caseStudy3.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("digital.caseStudy3.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("digital.caseStudy3.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("digital.caseStudy3.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                onKeyDown={handleKeyDown}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                {t("digital.caseStudy3.callToAction.button")}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Digital;
